/**
 * The jump (spec 02 §3 Jump, Platforms, Wrap), ported from rebb64: takeoff D_222B, rise D_23EA,
 * apex and descent L2401 / L242A with the one-way landing test, the end D_2519, and D_EB3F, the first
 * fall step when a jump ends over nothing.
 */

import { airDrift, airMove, landed } from './air';
import { probe, solidRun, type Probe } from './probe';
import { JOY_LEFT, JOY_RIGHT } from './state';
import {
  DESCENT_END, FALL_STEP, GRID_Y, JUMP_STEPS, JUMP_TABLE, LAND_MIN_Y_JUMP, LEVEL_92_INDEX, RISE_WRAP_BELOW,
  START_Y, WRAP_BOTTOM, WRAP_SPAN, WRAP_TOP,
} from './tuning';
import type { Player, World } from '../types';

/**
 * D_222B: take off. This tick does not move. A direction held while already facing it (by the
 * tick-start frame: 0-3 right, 4-7 left, never while blowing) drifts the whole jump; held while facing
 * the other way it does nothing, so the jump is vertical. The frame takes its jump bit unless blowing.
 */
export function startJump(p: Player, frame0: number): void {
  const m = p.motion;
  m.jump = JUMP_STEPS;
  m.driftLeft = (m.joy & JOY_LEFT) !== 0 && frame0 >= 4 && frame0 < 8;
  m.driftRight = (m.joy & JOY_RIGHT) !== 0 && frame0 < 4;
  if (m.frame < 8) m.frame |= 2;
}

/**
 * D_23EA: one tick of a jump. Rising (15 steps, $ACCD read backwards) has no tile collision at all
 * and wraps below Y 5 to the bottom. At the apex the jump frames become walk frames; then the same
 * table forwards (the first step 0: the apex tick). Descending, each time Y crosses an 8-px line
 * ((Y - $2D) & 7 below the tick-start fine offset $24): a solid tile beside the lower body lets the
 * player through; otherwise a solid tile under the feet lands them, Y snapped to the line.
 */
export function jumpTick(world: World, p: Player, pr: Probe, frame0: number): void {
  const m = p.motion;
  if (m.jump > 0) {
    let y = (p.y - (JUMP_TABLE[m.jump] ?? 0)) & 0xff;
    m.jump--;
    if (y < RISE_WRAP_BELOW) y += WRAP_SPAN;
    p.y = y;
    airMove(world, p, frame0);
    return;
  }
  if (m.descent < 0) {
    if ((m.frame >= 2 && m.frame < 4) || (m.frame >= 6 && m.frame < 8)) m.frame &= 5;
    m.descent = 0;
  }
  if (m.descent === DESCENT_END) {
    jumpEnd(world, p, pr);
    return;
  }
  let y = p.y + (JUMP_TABLE[m.descent] ?? 0);
  m.descent++;
  if (y >= WRAP_BOTTOM) y -= WRAP_SPAN;
  p.y = y;
  if (y < LAND_MIN_Y_JUMP || ((y - GRID_Y) & 7) >= pr.fy) {
    airMove(world, p, frame0);
    return;
  }
  const at = probe(p.x, y);
  if (!solidRun(world, at, 0x29) && solidRun(world, at, 0x51)) {
    p.y = (((y - GRID_Y) & 0xf8) + GRID_Y) & 0xff;
    jumpEnd(world, p, at);
    return;
  }
  airDrift(world, p, at, frame0);
}

/**
 * D_2519: the jump is over (landed, or the descent table ran out). X is rounded to even - up when
 * the frame is below 4 or $A and over, down otherwise (the reverse of L25BF for the blow frames, as
 * the C64 has it). Floor under the feet and none beside the lower body: standing. Otherwise the player
 * falls, starting at once (D_EB3F); on level 92 a player at the start height stays put.
 */
export function jumpEnd(world: World, p: Player, pr: Probe): void {
  const m = p.motion;
  m.jump = -1;
  m.descent = -1;
  const up = m.frame < 4 || m.frame >= 0x0a;
  p.x = up ? (p.x + 1) & 0xfe : p.x & 0xfe;
  if (!solidRun(world, pr, 0x29) && solidRun(world, pr, 0x51)) {
    landed(world, p);
    return;
  }
  if (world.levelIndex === LEVEL_92_INDEX && p.y === START_Y) {
    landed(world, p);
    return;
  }
  m.falling = true;
  fallStart(world, p, pr);
}

/**
 * D_EB3F: the fall's first step on the tick the jump ends, sharing the monsters' fall code: the
 * landing test from Y $1F (not $26) and no X rounding; then L_EB0F, the monsters' frame animation,
 * which for a player (D_8778 = D_8750 = 0) adds 1 to the frame when the counter reaches 2 - so a jump
 * that ends in a fall can leave a jump frame showing (C64 quirk, kept).
 */
function fallStart(world: World, p: Player, pr: Probe): void {
  const m = p.motion;
  let y = (p.y + FALL_STEP) & 0xff;
  if (y === WRAP_BOTTOM) y = WRAP_TOP;
  else if (y >= LAND_MIN_Y_JUMP && ((y - GRID_Y) & 7) === 0 && !solidRun(world, pr, 0x51)
    && solidRun(world, pr, 0x79)) {
    m.falling = false;
    landed(world, p);
  }
  p.y = y;
  m.anim = (m.anim + 1) & 0xff;
  if (m.anim < 2) return;
  m.anim = 0;
  m.frame = (m.frame + 1) & 0xff;
}
